import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ContactDetail, ContactPickItem } from '@tww/shared';
import { api, ApiRequestError } from '../../api/client';
import { useCategoryDefs, useLookups } from '../../api/hooks';
import { Modal } from '../../ui/Modal';
import { ComboField, Select, TextInput } from '../../ui/fields';
import { useToast } from '../../ui/Toast';

/** Schnellanlage aus dem ContactPicker: Name, Kategorie, Region, Telefon, E-Mail (Plan 02 Abschnitt 5.3) */
export function ContactQuickCreate({ initialName, category, region, onClose, onCreated }: { initialName?: string; category?: string; region?: string; onClose: () => void; onCreated: (c: ContactPickItem) => void }) {
  const defs = useCategoryDefs();
  const lookups = useLookups();
  const toast = useToast();
  const qc = useQueryClient();
  const def = defs.data?.find((d) => d.key === category);
  const orgOnly = def ? !def.kinds_allowed.includes('person') : false;
  const [kind, setKind] = useState<'person' | 'organisation'>(orgOnly ? 'organisation' : 'person');
  const [name, setName] = useState(initialName ?? '');
  const [cat, setCat] = useState(category ?? '');
  const [reg, setReg] = useState(region ?? '');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const regions = lookups.data?.find((l) => l.key === 'regions')?.values ?? [];

  const create = useMutation({
    mutationFn: () => {
      const parts = name.trim().split(/\s+/);
      const body =
        kind === 'person'
          ? { kind, first_name: parts.slice(0, -1).join(' ') || null, last_name: parts[parts.length - 1] ?? '' }
          : { kind, org_name: name.trim() };
      return api.post<ContactDetail>('/contacts', { ...body, region: reg || null, phone: phone || null, email: email || null, categories: [{ category_key: cat, attributes: {} }] });
    },
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contacts-pick'] });
      toast('Kontakt angelegt');
      onCreated({ id: c.id, display_name: c.display_name, sub: c.city, rating_grade: c.rating_grade, rating_reason: c.rating_reason, city: c.city, region: c.region, last_price: null, categories: c.categories });
    },
    onError: (e) => {
      if (e instanceof ApiRequestError) setErrors(e.fields ?? { _: e.message });
      else toast('Anlegen fehlgeschlagen', 'error');
    },
  });
  // Knopf und Enter prüfen dasselbe
  const canCreate = Boolean(name.trim() && cat) && !create.isPending;
  const submit = () => canCreate && create.mutate();
  return (
    <Modal
      title="Kontakt schnell anlegen"
      subtitle="Nur das Nötigste; alles Weitere später in der Akte"
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" type="button" onClick={submit} disabled={!canCreate}>
            Anlegen
          </button>
        </>
      }
    >
      <div className="form one">
        <div className="field full">
          <label>Art</label>
          <div className="seg" role="radiogroup" aria-label="Art">
            <button type="button" role="radio" aria-checked={kind === 'person'} className={kind === 'person' ? 'on' : ''} onClick={() => setKind('person')} disabled={orgOnly}>
              Person
            </button>
            <button type="button" role="radio" aria-checked={kind === 'organisation'} className={kind === 'organisation' ? 'on' : ''} onClick={() => setKind('organisation')}>
              Organisation
            </button>
          </div>
        </div>
        <TextInput full label={kind === 'person' ? 'Vor- und Nachname' : 'Name'} value={name} onChange={setName} required autoFocus error={errors.last_name ?? errors.org_name} onEnter={submit} />
        <Select label="Kategorie" value={cat} onChange={setCat} required options={(defs.data ?? []).filter((d) => d.is_active && d.kinds_allowed.includes(kind)).map((d) => ({ value: d.key, label: d.name }))} error={errors.categories} />
        <ComboField label="Region" value={reg} onChange={setReg} options={regions.filter((r) => r.is_active).map((r) => ({ value: r.code, label: r.label }))} newHint="wird als neue Region gespeichert" />
        <TextInput label="Telefon" value={phone} onChange={setPhone} />
        <TextInput label="E-Mail" value={email} onChange={setEmail} error={errors.email} />
        {errors._ && <div className="note bad full">{errors._}</div>}
      </div>
    </Modal>
  );
}
